<template>
  <b-overlay :show="loading" opacity="0.8">
    <import-diet-plan-file-modal
      :consultation-id="consultationId"
      :is-import-file-active.sync="isImportFileActive"
      @onImport="handleImport"
      @refetchData="handleRefresh"
    />

    <b-card class="rounded-lg">
      <template #header>
        <div class="d-flex justify-content-between align-items-center w-100 pb-1" style="border-bottom: 2px solid rgb(238, 238, 238);">
          <h4 class="text-dark m-0">
            Diet Plan Uploads{{ records.length > 0 ? `(${records.length})` : '' }}
          </h4>
          
          <div class="d-flex align-items-center justify-content-start">
            <div v-if="can('diet_plan_uploads:CanAddNew') && currentUserProfession === 'dietician'" class="d-flex align-items-center">
              <b-button size="sm" variant="link" class="p-0" @click="onImportFile(getValueFromSource(consultationData, '_id'))">
                <feather-icon
                  icon="PlusCircleIcon"
                  size="25"
                />
              </b-button>
            </div>
          </div>
        </div>
      </template>
      
      
      <div class="" style="height: 480px; overflow-y: scroll;">
        <b-overlay :show="loading" opacity="0.5">  
          <div>
            <list-table
              :records="records" 
              :columns="tableColumns"
              :current-page-number="1"
              :show-import-button="false"
              :show-export-button="false"
              :show-search="false"
              :total-records="totalRecords"
            >

            <template #cell(name)="data">
              <b-link
                v-if="getValueFromSource(data.item, 'name')"
                pill
                class="text-capitalize"
                :href="getValueFromSource(data.item, 'path')"
                target="_blank"
              >
                {{ getValueFromSource(data.item, 'name') }}
              </b-link>
              <span v-else>-</span>
            </template>


            <template #cell(status)="data">
              <b-badge
                v-if="getValueFromSource(data.item, 'status')"
                pill
                :variant="resolveStatusVariant(getValueFromSource(data.item, 'status'))"
                class="text-capitalize"
              >
                {{ getUserStatusText(getValueFromSource(data.item, 'status')) }}
              </b-badge>
              <span v-else>-</span>
            </template>
  
            <template #cell(actions)="data">
              <div v-if="currentUserProfession === 'dietician'">
                <b-dropdown
                  variant="link"
                  no-caret
                  :right="$store.state.appConfig.isRTL"
                >
                  <template #button-content>
                    <feather-icon
                      icon="MoreVerticalIcon"
                      size="16"
                      class="align-middle text-body"
                    />
                  </template>
  
                  <b-dropdown-item @click="onDelete(data.item)">
                    <feather-icon icon="Trash2Icon" />
                    <span class="align-middle ml-50">Delete</span>
                  </b-dropdown-item>
                </b-dropdown>
              </div>
              <span v-else class="ml-2">-</span>
            </template>
            </list-table>
          </div>
        </b-overlay>
      </div>
    </b-card>
  </b-overlay>
</template>

<script>
import {
  BCard,
  BImg,
  BRow,
  BCol,
  BBadge,
  BAlert,
  BButton,
  BAvatar,
  BOverlay,
  BDropdown,
  BLink,
  BDropdownItem,
  BDropdownGroup
} from 'bootstrap-vue'

import { get, startCase } from 'lodash';

import Ripple from 'vue-ripple-directive'
import ListTable from "@/@core/components/ListTable/ListTable.vue";
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

import ImportDietPlanFileModal from '@core/components/import-diet-plan-file-modal/index.vue';

export default {
  components: {
    BImg,
    BRow,
    BCol,
    BCard,
    BBadge,
    BAlert,
    BAvatar,
    BButton,
    BLink,
    BOverlay,
    BDropdown,
    BDropdownItem,
    BDropdownGroup,

    ListTable,
    ImportDietPlanFileModal,
  },
  directives: {
    Ripple,
  },
  props: {
    consultationData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      isImportFileActive: false,

      toggleModal: false,

      selectedFile: null,
      selectedPath: null,
      consultationId: null,
      uploadId: null,

      records: [],
      tableColumns: [
        { key: 'name', label: 'Name', sortable: false, class: 'name-column' },
        { 
          key: 'size',
          label: 'Size', 
          sortable: false,
          formatter: val => {
            const sizeInBytes = parseInt(val, 10);
            const sizeInKB = sizeInBytes / 1024;
            const sizeInMB = sizeInBytes / 1048576;
            return sizeInMB >= 1 ? `${sizeInMB.toFixed(2)} MB` : `${sizeInKB.toFixed(2)} KB`;
          } 
        },
        { key: 'created', label: 'Date', sortable: false, formatter: val => `${this.formatDate(val, 'MM-yy')}`, class: 'w-10' },
        { key: 'actions' },
      ],
    }
  },
  computed: {
    totalRecords: {
      get(){
        return this.$store.getters[`navigation/totalRecords`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value)
      }
    },
  },
  created() {
    this.fetchAllData();
  },
  methods: {
    async fetchAllData() {
      try {
        this.loading = true; 

        const query = {
          page: 1,
          limit: 100,
          consultation_id: this.consultationData._id,
        }

        const response = await this.useJwt().mainService.dietPlanUploads.list(query)
        const { data, pagination } = response.data;
        this.records = data;
        this.totalRecords = pagination.totalRecords
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        console.log("error_message", error_message)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async onDelete(record){
      try {
        this.loading = true;

        if (!this.can(`diet_plan_uploads:CanDelete`)) {
          return
        }

        await this.useJwt().mainService.dietPlanUploads.delete(record.id);
        this.fetchAllData();

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: `Upload deleted successfully`
          },
        });
      } catch (error){
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },
    handleRefresh(){
      this.fetchAllData()
    },
    onImportFile(consultation_id){
      this.isImportFileActive = true;
      this.consultationId = consultation_id;
    },
    handleImport(val) {
      this.selectedFile = val.file;
      this.selectedPath = val.path;
    },
    onEdit(record){
      this.editingData = record;
      this.toggleModal = true;
    },
    onAttachNote(consultation_id) {
      this.toggleModal = true;
      this.consultationId = consultation_id;
    },
    onRefresh() {
      this.$emit('refresh')
      this.fetchAllData();
    },
    startCase
  }
}
</script>

<style scoped>
.name-column {
  max-width: 200px !important;
  white-space: normal !important;
  word-wrap: break-word !important;
}

.w-10 {
  width: 13% !important;
}
</style>
